"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { z } from "zod";
import ClassicLoader from "@/components/common/classic-loader";
import { FormInput } from "@/components/form/form-input";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { AuthDivider } from "@/features/auth/components/elements/auth-divider";
import { AuthInfoBox } from "@/features/auth/components/elements/auth-info-box";
import { SocialSignInButtons } from "@/features/auth/components/elements/social-sign-in-buttons";
import { AUTH_PRIMARY_BUTTON } from "@/features/auth/lib/auth-button-styles";
import type { OAuthProvider } from "@/features/auth/types/auth-flow.type";

// Domain part of an e-mail address: at least one label followed by a TLD.
const EMAIL_DOMAIN_PATTERN = /@[^@\s]+\.[a-z]{2,}$/i;

const formSchema = z.object({
  email: z.string().superRefine((value, ctx) => {
    const email = value.trim();

    if (email.length === 0) {
      ctx.addIssue({
        code: "custom",
        message: "auth.email.validators.required",
      });
      return;
    }

    if (!email.includes("@")) {
      ctx.addIssue({
        code: "custom",
        message: "auth.email.validators.missingAt",
      });
      return;
    }

    if (!EMAIL_DOMAIN_PATTERN.test(email)) {
      ctx.addIssue({
        code: "custom",
        message: "auth.email.validators.missingDomain",
      });
    }
  }),
});

type EmailStepProps = {
  defaultEmail?: string;
  isSubmitting: boolean;
  /** The dialog carries the title in its header, the page needs it inline. */
  showHeading?: boolean;
  onSubmit: (email: string) => void;
  onProviderSelect: (provider: OAuthProvider) => void;
};

export const EmailStep = ({
  defaultEmail = "",
  isSubmitting,
  showHeading = true,
  onSubmit,
  onProviderSelect,
}: EmailStepProps) => {
  const t = useTranslations("auth");

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { email: defaultEmail },
  });

  return (
    <div className="flex flex-col gap-6">
      {showHeading && (
        <AuthInfoBox
          description={t("email.description")}
          title={t("email.title")}
        />
      )}

      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((values) =>
            onSubmit(values.email.trim()),
          )}
        >
          {/* Deliberately not type="email": the browser's own validation
              bubble would preempt the wording the design asks for. */}
          <FormInput
            autoCapitalize="off"
            autoComplete="email"
            autoCorrect="off"
            fieldLabel={t("email.label")}
            fieldName="email"
            form={form}
            inputMode="email"
            required
          />
          <Button
            className={AUTH_PRIMARY_BUTTON}
            data-pw="sign-in-modal-button"
            disabled={isSubmitting}
            type="submit"
          >
            {t("email.submit")}
            {isSubmitting && <ClassicLoader className="ml-2" />}
          </Button>
        </form>
      </Form>

      <AuthDivider />

      <SocialSignInButtons
        disabled={isSubmitting}
        onSelect={onProviderSelect}
      />
    </div>
  );
};
